/**
 * Phone Number Validation
 *
 * Uses libphonenumber-js for formatting helpers.
 * Structural validation uses a digit-count check rather than numbering-plan
 * lookup so that test/placeholder numbers like (555) 123-4567 are accepted —
 * appropriate for a lead-capture form where we only need a callable-looking
 * number, not a verified subscriber line.
 */

import { parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js';

/**
 * Validate if a phone number has the correct structure for a given country.
 *
 * For US/CA (NANP): accepts any 10-digit number, with or without +1 prefix,
 * in any common formatting (spaces, dashes, parentheses).
 * For other countries: delegates to libphonenumber-js isPossible check.
 *
 * @example
 * validatePhoneNumber('(555) 123-4567', 'US') // true
 * validatePhoneNumber('+1 555 123 4567', 'US') // true
 * validatePhoneNumber('invalid', 'US')          // false
 * validatePhoneNumber('123', 'US')              // false
 */
export function validatePhoneNumber(value: string | undefined, country: CountryCode = 'US'): boolean {
  if (!value) return false;

  if (country === 'US' || country === 'CA') {
    // Strip all non-digit characters, then strip the leading +1 country code only
    // when the result is 11 digits (to avoid eating the first digit of the area code)
    const allDigits = value.replace(/\D/g, '');
    const digits = allDigits.length === 11 && allDigits.startsWith('1')
      ? allDigits.slice(1)
      : allDigits;
    return digits.length === 10;
  }

  // For non-NANP countries fall back to libphonenumber-js possibility check
  // (isPossible is less strict than isValid — it checks length, not subscriber ranges)
  const parsed = parsePhoneNumberFromString(value, country);
  return parsed?.isPossible() ?? false;
}


/**
 * Format a phone number as the user types with visible template/mask
 * 
 * Shows the phone number format template as the user types, making it clear
 * what format is expected. For US/CA: (XXX) XXX-XXXX
 * 
 * @param value - Partial or complete phone number
 * @param country - ISO country code (default: 'US')
 * @returns Formatted phone number with visible template
 * 
 * @example
 * formatPhoneNumberAsYouType('', 'US')        // ''
 * formatPhoneNumberAsYouType('1', 'US')       // '(1'
 * formatPhoneNumberAsYouType('123', 'US')     // '(123) '
 * formatPhoneNumberAsYouType('1234', 'US')    // '(123) 4'
 * formatPhoneNumberAsYouType('1234567', 'US') // '(123) 456-7'
 * formatPhoneNumberAsYouType('1234567890', 'US') // '(123) 456-7890'
 */
export function formatPhoneNumberAsYouType(value: string, country: CountryCode = 'US'): string {
  if (!value) return value;
  
  try {
    // Remove all non-digit characters for parsing
    const digitsOnly = value.replace(/\D/g, '');
    
    if (!digitsOnly) return '';
    
    // For partial numbers, do basic formatting based on country
    if (country === 'US' || country === 'CA') {
      // North American format: (XXX) XXX-XXXX
      const digits = digitsOnly.slice(0, 10); // Max 10 digits
      
      if (digits.length <= 3) {
        // Show opening bracket as soon as user starts typing
        return digits.length > 0 ? `(${digits}` : digits;
      } else if (digits.length <= 6) {
        // After 3 digits, close bracket and add space
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        // After 6 digits, add hyphen
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
    }
    
    // For other countries, try to parse and format
    const phoneNumber = parsePhoneNumberFromString(digitsOnly, country);
    if (phoneNumber) {
      return phoneNumber.formatNational();
    }
    
    // Fallback: just return the digits
    return digitsOnly;
  } catch {
    return value;
  }
}
